import React, { useEffect, useState } from "react";
import { http, formatError, pruneEmpty } from "@/lib/api";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Plus, ArrowRight } from "lucide-react";
import { toast } from "sonner";

const STAGES = ["new", "contacted", "qualified", "proposal", "negotiation", "won", "lost"];
const STAGE_STYLES = {
  new: "border-slate-300 text-slate-600 bg-slate-50",
  contacted: "border-blue-300 text-blue-700 bg-blue-50",
  qualified: "border-cyan-300 text-cyan-700 bg-cyan-50",
  proposal: "border-amber-300 text-amber-700 bg-amber-50",
  negotiation: "border-orange-300 text-orange-700 bg-orange-50",
  won: "border-green-400 text-green-700 bg-green-50",
  lost: "border-red-300 text-red-700 bg-red-50",
};

export default function OwnerCRM() {
  const [pipe, setPipe] = useState(null);

  async function load() {
    const { data } = await http.get("/ops/crm/pipeline");
    setPipe(data);
  }
  useEffect(() => { load(); }, []);

  async function moveStage(leadId, stage) {
    try {
      await http.patch(`/ops/crm/leads/${leadId}`, { stage });
      toast.success(`Moved to ${stage}`);
      load();
    } catch (e) { toast.error(formatError(e.response?.data?.detail)); }
  }

  if (!pipe) return <div className="text-muted-foreground">Loading…</div>;

  const totalValue = pipe.order.reduce((s, k) => s + (pipe.stages[k]?.value_cents || 0), 0);

  return (
    <div className="space-y-6" data-testid="owner-crm">
      <div className="flex items-center justify-between">
        <div>
          <div className="text-xs uppercase tracking-widest text-muted-foreground">CRM</div>
          <h1 className="font-display text-3xl font-bold text-primary">Sales Pipeline</h1>
          <p className="text-sm text-muted-foreground mt-1">
            {pipe.order.reduce((s, k) => s + (pipe.stages[k]?.count || 0), 0)} total leads · <b>${(totalValue / 100).toLocaleString()}</b> pipeline value
          </p>
        </div>
        <NewLeadDialog onCreated={load} />
      </div>

      {/* Kanban board */}
      <div className="grid grid-cols-2 lg:grid-cols-7 gap-3 overflow-x-auto">
        {pipe.order.map((s) => {
          const col = pipe.stages[s] || { count: 0, leads: [], value_cents: 0 };
          return (
            <div key={s} className={`rounded-xl border p-3 min-h-[300px] ${STAGE_STYLES[s]}`}>
              <div className="flex items-center justify-between mb-3">
                <div className="text-xs uppercase tracking-wider font-semibold">{s}</div>
                <Badge variant="outline" className="text-[10px]">{col.count}</Badge>
              </div>
              <div className="text-[10px] text-muted-foreground mb-2">${(col.value_cents / 100).toLocaleString()} MRR</div>
              <div className="space-y-2">
                {col.leads.map((l) => (
                  <LeadCard key={l.id} lead={l} stages={STAGES} onMove={(st) => moveStage(l.id, st)} />
                ))}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

function LeadCard({ lead, stages, onMove }) {
  const [openMove, setOpenMove] = useState(false);
  return (
    <Card className="border border-border/60 bg-white" data-testid={`lead-card-${lead.id}`}>
      <CardContent className="p-3 space-y-1.5">
        <div className="font-display font-semibold text-primary text-sm leading-tight">{lead.name}</div>
        <div className="flex items-center gap-1 text-[10px] text-muted-foreground">
          <Badge variant="outline" className="text-[9px]">{lead.type}</Badge>
          {lead.est_mrr_cents ? <span>${(lead.est_mrr_cents / 100).toLocaleString()} MRR</span> : null}
        </div>
        {lead.notes && <div className="text-[11px] text-muted-foreground line-clamp-2">{lead.notes}</div>}
        <Dialog open={openMove} onOpenChange={setOpenMove}>
          <DialogTrigger asChild>
            <Button size="sm" variant="ghost" className="w-full h-6 text-[10px] hover:bg-primary/5" data-testid={`lead-move-${lead.id}`}>Move…</Button>
          </DialogTrigger>
          <DialogContent className="max-w-xs">
            <DialogHeader><DialogTitle>Move {lead.name}</DialogTitle></DialogHeader>
            <div className="flex flex-wrap gap-2">
              {stages.filter((s) => s !== lead.stage).map((s) => (
                <Button key={s} size="sm" variant="outline" onClick={() => { onMove(s); setOpenMove(false); }}
                        className="capitalize" data-testid={`lead-move-to-${s}`}>{s}</Button>
              ))}
            </div>
          </DialogContent>
        </Dialog>
      </CardContent>
    </Card>
  );
}

function NewLeadDialog({ onCreated }) {
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ name: "", email: "", phone: "", type: "parent", source: "cold_outreach", stage: "new", est_mrr_cents: 0, notes: "" });
  async function submit() {
    try {
      const payload = pruneEmpty({ ...form, est_mrr_cents: Number(form.est_mrr_cents) || 0 });
      await http.post("/ops/crm/leads", payload);
      toast.success("Lead added");
      setOpen(false);
      setForm({ name: "", email: "", phone: "", type: "parent", source: "cold_outreach", stage: "new", est_mrr_cents: 0, notes: "" });
      onCreated();
    } catch (e) { toast.error(formatError(e.response?.data?.detail)); }
  }
  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild><Button className="bg-primary hover:bg-primary/90" data-testid="new-lead-btn"><Plus size={14} className="mr-1" /> New lead</Button></DialogTrigger>
      <DialogContent className="max-w-md">
        <DialogHeader><DialogTitle>New sales lead</DialogTitle></DialogHeader>
        <div className="space-y-3">
          <div><Label className="text-xs">Name / company</Label><Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="new-lead-name" /></div>
          <div className="grid grid-cols-2 gap-3">
            <div><Label className="text-xs">Email</Label><Input type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} /></div>
            <div><Label className="text-xs">Phone</Label><Input value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} /></div>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div><Label className="text-xs">Type</Label>
              <Select value={form.type} onValueChange={(v) => setForm({ ...form, type: v })}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="parent">Parent / consumer</SelectItem>
                  <SelectItem value="community">Apartment community</SelectItem>
                  <SelectItem value="university">University</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div><Label className="text-xs">Source</Label><Input value={form.source} onChange={(e) => setForm({ ...form, source: e.target.value })} /></div>
          </div>
          <div><Label className="text-xs">Est. MRR (cents)</Label><Input type="number" value={form.est_mrr_cents} onChange={(e) => setForm({ ...form, est_mrr_cents: e.target.value })} data-testid="new-lead-mrr" /></div>
          <div><Label className="text-xs">Notes</Label><Textarea rows={2} value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} /></div>
          <Button onClick={submit} disabled={!form.name} className="w-full bg-primary hover:bg-primary/90" data-testid="new-lead-save">Add lead</Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
